import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaExclamationTriangle, FaUndoAlt } from 'react-icons/fa';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { NS } from '../../../U3141';
import type {
  U3141AnnotatedLotChainIndexMergeDryRunLot,
  U3141AnnotatedLotChainIndexMergeDryRunResponse,
} from '../../../U3141Service';
import {
  u3141CanLaunchAnnotatedLotChainMergeFromDryRun,
  u3141DisplayAnnotatedMergeBlockReason,
} from '../../../tools/u3141AnnotatedLotChainIndexMergeHelpers';

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

export interface U3141GenerateAnnotatedLotChainDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dryRunResult: U3141AnnotatedLotChainIndexMergeDryRunResponse | null;
  dryRunLoading?: boolean;
  launchLoading?: boolean;
  onGenerate: () => Promise<void>;
}

function statusBadgeClass(status: U3141AnnotatedLotChainIndexMergeDryRunLot['status']): string {
  switch (status) {
    case 'ok':
      return 'bg-green-100 text-green-900';
    case 'missing_index':
      return 'bg-red-100 text-red-800';
    case 'will_be_masked':
      return 'bg-amber-100 text-amber-900';
    case 'anchor_not_visible':
      return 'bg-violet-100 text-violet-900';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export const U3141GenerateAnnotatedLotChainDialog: React.FC<U3141GenerateAnnotatedLotChainDialogProps> = ({
  isOpen,
  onClose,
  dryRunResult,
  dryRunLoading = false,
  launchLoading = false,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const busy = dryRunLoading || launchLoading;
  const summary = dryRunResult?.summary;
  const lots = dryRunResult?.lots ?? [];
  const canLaunch = u3141CanLaunchAnnotatedLotChainMergeFromDryRun(summary);
  const blockReason = u3141DisplayAnnotatedMergeBlockReason(summary?.blockReason);
  const generateDisabledHint =
    summary && summary.canLaunchJob && !canLaunch
      ? t('msgGenerateAnnotatedLotChainAllIndexesRequired', { ns: NS })
      : !canLaunch && blockReason
        ? blockReason
        : undefined;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dlgGenerateAnnotatedLotChainTitle', { ns: NS })}
      width="large"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          <p className="text-sm text-gray-700 mb-4">{t('dlgGenerateAnnotatedLotChainIntro', { ns: NS })}</p>
          {dryRunLoading ? (
            <p className="text-sm text-gray-500">{t('msgGenerateAnnotatedLotChainDryRunLoading', { ns: NS })}</p>
          ) : null}
          {summary ? (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
              <p>{t('msgGenerateAnnotatedLotChainSummary', { ns: NS, ...summary })}</p>
              {(summary.annotationsNewerThanChainCount ?? 0) > 0 ? (
                <p className="mt-2 text-amber-900">
                  {t('msgGenerateAnnotatedLotChainAnnotationsNewer', {
                    ns: NS,
                    count: summary.annotationsNewerThanChainCount ?? 0,
                  })}
                </p>
              ) : null}
              {!canLaunch && blockReason && !summary.canLaunchJob ? (
                <p className="mt-2 font-medium text-red-800">{blockReason}</p>
              ) : null}
              {!canLaunch && summary.canLaunchJob && (summary.missingIndexCount > 0 || summary.maskedCount > 0) ? (
                <p className="mt-2 font-medium text-amber-900">
                  {t('msgGenerateAnnotatedLotChainAllIndexesRequired', { ns: NS })}
                </p>
              ) : null}
            </div>
          ) : null}
          {!dryRunLoading && lots.length === 0 && dryRunResult ? (
            <p className="text-sm text-gray-500">{t('msgGenerateAnnotatedLotChainNoLots', { ns: NS })}</p>
          ) : null}
          {lots.length > 0 ? (
            <div className="overflow-x-auto rounded-sm border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t('colGenerateAnnotatedLotChainLot', { ns: NS })}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t('colGenerateAnnotatedLotChainStatus', { ns: NS })}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t('colGenerateAnnotatedLotChainAnnotations', { ns: NS })}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {lots.map((lot) => (
                    <tr key={lot.childLotChainId}>
                      <td className="px-3 py-2 text-gray-800">{lot.lotLabel}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(lot.status)}`}
                        >
                          {t(`lblAnnotatedMergeDryRunStatus.${lot.status}`, {
                            ns: NS,
                            defaultValue: lot.status,
                          })}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {lot.annotationsNewerThanChain ? (
                          <span
                            className="inline-flex items-center gap-1 text-amber-700"
                            title={t('lblAnnotatedChainOutdatedTooltip', { ns: NS })}
                          >
                            <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            {t('lblGenerateAnnotatedLotChainAnnotationsNewer', { ns: NS })}
                          </span>
                        ) : (
                          <span className="text-gray-400">{t('lblGenerateAnnotatedLotChainAnnotationsUpToDate', { ns: NS })}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" className={cancelButtonClass} onClick={handleClose} disabled={busy}>
            <FaUndoAlt className="h-4 w-4" />
            {t('dlgLblCancel', { ns: NS })}
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void onGenerate()}
            disabled={!canLaunch || busy || dryRunLoading}
            title={generateDisabledHint}
          >
            <FaCheck className="h-4 w-4" />
            {launchLoading
              ? t('btnGenerateAnnotatedLotChainRunning', { ns: NS })
              : t('btnGenerateAnnotatedLotChain', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
